import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  IconButton,
  Card,
  CardMedia,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
} from '@mui/material';
import { Delete as DeleteIcon, CloudUpload as CloudUploadIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import axios from '../utils/axios';

// Map container style - removed unused variable

// Blue circle marker for gym
const gymMarkerIcon = {
  path: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z',
  fillColor: '#1976d2',
  fillOpacity: 1,
  strokeColor: '#FFFFFF',
  strokeWeight: 3,
  scale: 1.8,
  anchor: { x: 12, y: 12 }
};

const mapOptions = {
  zoom: 18,
  mapTypeControl: true,
  streetViewControl: true,
  fullscreenControl: true,
  zoomControl: true,
  gestureHandling: 'cooperative',
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'on' }]
    },
    {
      featureType: 'poi.business',
      elementType: 'labels',
      stylers: [{ visibility: 'on' }]
    },
    // Hide other business labels to make our gym stand out
    {
      featureType: 'poi.business',
      elementType: 'labels.text',
      stylers: [{ visibility: 'off' }]
    }
  ]
};

const CreateListing = () => {
  const navigate = useNavigate();
  
  // Use the hook for API loading
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places', 'geometry'] // Ensure places library is loaded
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    maxPeople: '',
    address: '',
    personalTrainingPremium: '',
    photos: [],
    amenities: {
      parking: false,
      showers: false,
      wifi: false,
      personalTraining: false,
      refreshments: false,
      towelService: false,
      saunaSteamRoom: false
    },
    availableHours: {
      monday: { start: '09:00', end: '21:00', enabled: true },
      tuesday: { start: '09:00', end: '21:00', enabled: true },
      wednesday: { start: '09:00', end: '21:00', enabled: true },
      thursday: { start: '09:00', end: '21:00', enabled: true },
      friday: { start: '09:00', end: '21:00', enabled: true },
      saturday: { start: '09:00', end: '21:00', enabled: true },
      sunday: { start: '09:00', end: '21:00', enabled: true }
    },
    location: {
      lat: 0,
      lng: 0
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showMapPreview, setShowMapPreview] = useState(false);
  const [googleReady, setGoogleReady] = useState(false); // Track Google Maps full initialization

  // Handle address selection from autocomplete
  const handleAddressSelect = async (address) => {
    try {
      setSuggestionsLoading(true);
      setShowMapPreview(false);
      const results = await geocodeByAddress(address);
      const latLng = await getLatLng(results[0]);
      
      setFormData(prev => ({
        ...prev,
        address,
        location: latLng
      }));
      setError(null);
      setShowMapPreview(true); // Show map after geocoding
    } catch (error) {
      console.error('Error getting address details:', error);
      setError('Could not find location for this address. Please enter a valid address.');
      setShowMapPreview(false);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    if (name.startsWith('amenities.')) {
      const amenity = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        amenities: {
          ...prev.amenities,
          [amenity]: checked
        }
      }));
    } else if (name.startsWith('availableHours.')) {
      const [, day, field] = name.split('.');
      setFormData(prev => ({
        ...prev,
        availableHours: {
          ...prev.availableHours,
          [day]: {
            ...prev.availableHours[day],
            [field]: field === 'enabled' ? checked : value
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    setLoading(true);
    setError(null);

    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('photos', file);

        const response = await axios.post('/api/gyms/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        return response.data.urls[0];
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...uploadedUrls]
      }));
    } catch (err) {
      console.error('Error uploading photos:', err);
      setError('Failed to upload photos. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handlePreview = () => {
    setPreviewOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields with specific error messages
      const missingFields = [];
      
      if (!formData.name?.trim()) missingFields.push('Name');
      if (!formData.description?.trim()) missingFields.push('Description');
      if (!formData.price || isNaN(parseFloat(formData.price))) missingFields.push('Price per hour');
      if (!formData.maxPeople || isNaN(parseInt(formData.maxPeople))) missingFields.push('Maximum number of people');
      if (!formData.address?.trim()) missingFields.push('Address');
      if (!formData.location.lat || !formData.location.lng) missingFields.push('Valid address selection');

      if (missingFields.length > 0) {
        setError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
        setLoading(false);
        return;
      }

      // Validate personal training premium if personal training is enabled
      if (formData.amenities.personalTraining && (!formData.personalTrainingPremium || isNaN(parseFloat(formData.personalTrainingPremium)))) {
        setError('Please enter a valid personal training premium amount');
        setLoading(false);
        return;
      }

      const response = await axios.post('/api/gyms', {
        ...formData,
        pricePerHour: parseFloat(formData.price),
        maxPeople: parseInt(formData.maxPeople, 10),
        personalTrainingPremium: formData.amenities.personalTraining ? parseFloat(formData.personalTrainingPremium) : 0
      });
      navigate(`/gyms/${response.data._id}`);
    } catch (err) {
      console.error('Error creating listing:', err);
      
      // Handle server error responses with detailed messages
      if (err.response?.data?.error) {
        const serverError = err.response.data;
        if (serverError.details && Array.isArray(serverError.details)) {
          // Format the detailed error message
          const errorDetails = serverError.details.join(', ');
          setError(`${serverError.error}: ${errorDetails}`);
        } else {
          setError(serverError.error);
        }
      } else {
        setError('Failed to create listing. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderPreview = () => (
    <Box>
      <Typography variant="h5" gutterBottom>{formData.name}</Typography>
      <Typography variant="body1" paragraph>{formData.description}</Typography>
      <Typography variant="h6">${formData.price}/hour</Typography>
      {formData.amenities.personalTraining && formData.personalTrainingPremium && (
        <Typography variant="body2" color="success.main">
          + ${formData.personalTrainingPremium}/hr for personal training
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary">
        Maximum people: {formData.maxPeople}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Address: {formData.address}
      </Typography>
      
      <Typography variant="subtitle1" gutterBottom>Amenities:</Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
        {Object.entries(formData.amenities).map(([key, value]) => (
          value && (
            <Chip
              key={key}
              label={key.charAt(0).toUpperCase() + key.slice(1)}
              color="primary"
              variant="outlined"
            />
          )
        ))}
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Available Hours:</Typography>
        <Grid container spacing={1}>
          {Object.entries(formData.availableHours).map(([day, schedule]) => (
            <Grid item xs={12} sm={6} md={4} key={day}>
              <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'capitalize', mb: 0.5 }}>
                  {day}
                </Typography>
                {schedule.enabled ? (
                  <Typography variant="body2" color="primary">
                    {schedule.start} - {schedule.end}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Closed
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {formData.photos.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Photos:</Typography>
          <Grid container spacing={1}>
            {formData.photos.map((photo, index) => (
              <Grid item xs={4} key={index}>
                <Card>
                  <CardMedia
                    component="img"
                    image={photo}
                    alt={`Gym photo ${index + 1}`}
                    sx={{ height: 140, objectFit: 'cover' }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Box sx={{ mt: 2, height: 300 }}>
        {isLoaded && formData.location.lat ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={formData.location}
            zoom={18}
            options={mapOptions}
          >
            <Marker
              position={formData.location}
              title={formData.name}
              icon={gymMarkerIcon}
            />
            
            {/* Blue-themed gym name overlay */}
            <InfoWindow
              position={{
                lat: formData.location.lat + 0.0001,
                lng: formData.location.lng
              }}
              options={{
                disableAutoPan: false,
                closeBoxURL: "", // Remove close button so it stays visible
                enableEventPropagation: true
              }}
            >
              <Box sx={{ 
                p: 1.5, 
                minWidth: 200,
                maxWidth: 300,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                borderRadius: '8px',
                color: 'white',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                    mb: 0.5,
                    lineHeight: 1.2
                  }}
                >
                  🏋️ {formData.name || 'Your Gym'}
                </Typography>
              </Box>
            </InfoWindow>
          </GoogleMap>
        ) : (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>Loading map...</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  // Enhanced Google Maps loading with retry mechanism
  useEffect(() => {
    const checkGoogleReady = () => {
      if (isLoaded && window.google && window.google.maps && window.google.maps.places) {
        setGoogleReady(true);
        return true;
      }
      return false;
    };

    if (loadError) {
      console.error('Google Maps API load error:', loadError);
      setError('Failed to load Google Maps. Please check your API key.');
    } else if (isLoaded) {
      // Initial check
      if (checkGoogleReady()) return;

      // Setup retry mechanism if places library not immediately available
      const maxAttempts = 5;
      let attempts = 0;
      
      const intervalId = setInterval(() => {
        attempts++;
        if (checkGoogleReady() || attempts >= maxAttempts) {
          clearInterval(intervalId);
          if (attempts >= maxAttempts) {
            setError('Google Maps Places library failed to load. Please refresh the page.');
          }
        }
      }, 500);
      
      return () => clearInterval(intervalId);
    }
  }, [isLoaded, loadError]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Create New Listing
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={4}
                required
              />
            </Grid>

            <Grid item xs={12}>
              {/* Only render PlacesAutocomplete when Google is fully ready */}
              {googleReady ? (
                <PlacesAutocomplete
                  value={formData.address}
                  onChange={(address) => setFormData(prev => ({ ...prev, address }))}
                  onSelect={handleAddressSelect}
                >
                  {({ getInputProps, suggestions, getSuggestionItemProps }) => (
                    <div>
                      <TextField
                        {...getInputProps({
                          fullWidth: true,
                          label: 'Address*',
                          placeholder: 'Enter the full address of your gym',
                          helperText: 'Start typing and select an address from the suggestions',
                          required: true,
                          name: 'address',
                        })}
                      />
                      <Box sx={{ position: 'relative' }}>
                        <Box sx={{
                          position: 'absolute',
                          zIndex: 1000,
                          width: '100%',
                          bgcolor: 'background.paper',
                          boxShadow: 3,
                          borderRadius: 1,
                          mt: 0.5,
                          maxHeight: 200,
                          overflowY: 'auto'
                        }}>
                          {suggestionsLoading && <Box sx={{ p: 2 }}>Loading addresses...</Box>}
                          {suggestions.map(suggestion => {
                            const style = {
                              backgroundColor: suggestion.active ? '#f0f0f0' : '#fff',
                              cursor: 'pointer',
                              padding: '10px',
                              borderBottom: '1px solid #eee'
                            };
                            return (
                              <div
                                {...getSuggestionItemProps(suggestion, { style })}
                                key={suggestion.placeId}
                              >
                                {suggestion.description}
                              </div>
                            );
                          })}
                        </Box>
                      </Box>
                    </div>
                  )}
                </PlacesAutocomplete>
              ) : (
                <TextField
                  fullWidth
                  label="Address*"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Enter the full address of your gym"
                  helperText="Loading Google Maps..."
                  required
                  name="address"
                  disabled={!isLoaded}
                />
              )}
            </Grid>

            {/* Map Preview */}
            {showMapPreview && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Location Preview
                </Typography>
                <Box sx={{ height: 300, width: '100%', mt: 1, mb: 2, borderRadius: 1, overflow: 'hidden', border: '2px solid #e0e0e0' }}>
                  {isLoaded && formData.location.lat ? (
                    <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '100%' }}
                      center={formData.location}
                      zoom={18}
                      options={mapOptions}
                    >
                      <Marker
                        position={formData.location}
                        title={formData.name}
                        icon={gymMarkerIcon}
                      />
                      
                      {/* Blue-themed gym name overlay */}
                      <InfoWindow
                        position={{
                          lat: formData.location.lat + 0.0001,
                          lng: formData.location.lng
                        }}
                        options={{
                          disableAutoPan: false,
                          closeBoxURL: "", // Remove close button so it stays visible
                          enableEventPropagation: true
                        }}
                      >
                        <Box sx={{ 
                          p: 1.5, 
                          minWidth: 200,
                          maxWidth: 300,
                          textAlign: 'center',
                          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                          borderRadius: '8px',
                          color: 'white',
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                        }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 'bold',
                              fontSize: '1.2rem',
                              textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                              mb: 0.5,
                              lineHeight: 1.2
                            }}
                          >
                            🏋️ {formData.name || 'Your Gym'}
                          </Typography>
                        </Box>
                      </InfoWindow>
                    </GoogleMap>
                  ) : (
                    <Box sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: '#f5f5f5'
                    }}>
                      <CircularProgress />
                      <Typography variant="body1" sx={{ ml: 2 }}>Loading map...</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price per hour"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleInputChange}
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Maximum number of people"
                name="maxPeople"
                type="number"
                value={formData.maxPeople}
                onChange={handleInputChange}
                required
                inputProps={{ min: 1 }}
              />
            </Grid>

            {/* Amenities Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Amenities
              </Typography>
              <Grid container spacing={2}>
                {Object.keys(formData.amenities).map((amenity) => (
                  <Grid item xs={6} sm={4} key={amenity}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.amenities[amenity]}
                          onChange={handleInputChange}
                          name={`amenities.${amenity}`}
                        />
                      }
                      label={amenity.charAt(0).toUpperCase() + amenity.slice(1)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Available Hours Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Available Hours
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set the days and hours when your gym is available for booking
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(formData.availableHours).map(([day, schedule]) => (
                  <Grid item xs={12} sm={6} md={4} key={day}>
                    <Paper elevation={1} sx={{ p: 2, border: schedule.enabled ? '2px solid #1976d2' : '2px solid #e0e0e0' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={schedule.enabled}
                            onChange={handleInputChange}
                            name={`availableHours.${day}.enabled`}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                            {day}
                          </Typography>
                        }
                        sx={{ mb: 1 }}
                      />
                      {schedule.enabled && (
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Open"
                              type="time"
                              name={`availableHours.${day}.start`}
                              value={schedule.start}
                              onChange={handleInputChange}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Close"
                              type="time"
                              name={`availableHours.${day}.end`}
                              value={schedule.end}
                              onChange={handleInputChange}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                            />
                          </Grid>
                        </Grid>
                      )}
                      {!schedule.enabled && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Closed
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Personal Training Premium */}
            {formData.amenities.personalTraining && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Personal Training Premium (per hour)"
                  name="personalTrainingPremium"
                  type="number"
                  value={formData.personalTrainingPremium}
                  onChange={handleInputChange}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                  InputProps={{
                    startAdornment: '$',
                  }}
                  helperText="Set the premium price for personal training (required when personal training is enabled)"
                  required
                />
              </Grid>
            )}

            {/* Photo Upload Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Photos
              </Typography>
              <Box sx={{ mb: 2 }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="photo-upload"
                  type="file"
                  multiple
                  onChange={handlePhotoUpload}
                />
                <label htmlFor="photo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    disabled={loading}
                  >
                    Upload Photos
                  </Button>
                </label>
              </Box>

              <Grid container spacing={2}>
                {formData.photos.map((photo, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="140"
                        image={photo}
                        alt={`Uploaded photo ${index + 1}`}
                        sx={{
                          objectFit: 'cover',
                          width: '100%',
                          height: '140px'
                        }}
                      />
                      <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleRemovePhoto(index)}
                          disabled={loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  onClick={handlePreview}
                  disabled={loading}
                  startIcon={<VisibilityIcon />}
                >
                  Preview
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  Create Listing
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Preview Listing</DialogTitle>
        <DialogContent>
          {renderPreview()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CreateListing;
